import { memo, type MutableRefObject } from 'react';

import { HighlightedRectangleCanvas } from './HighlightedRectangleCanvas.js';
import { datapointIndicatorLayout } from './XYChartExplore.sty.js';
import { Canvas } from '../../../../core/components/canvas/Canvas.js';
import { CanvasContext } from '../../../../core/components/canvas/CanvasContext.js';
import type { CanvasShape } from '../../../../core/components/canvas/types/canvas-shape.js';
import { useGridPlotSize } from '../../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { RECT_STROKE_WIDTH } from '../../../constants/defaults.js';
import { useBarData } from '../../../hooks/useBarData.js';
import { useRectData } from '../../../hooks/useRectData.js';
import { useXYChartData } from '../../../hooks/useXYChartData.js';
import { useXYChartRectSelection } from '../../../hooks/useXYChartRectSelection.js';
import { useXYChartScales } from '../../../hooks/useXYChartScales.js';
import { useSelectState } from '../../../store/selectors.js';
import { useTooltipMetadata } from '../hooks/useTooltipMetadata.js';
import { buildRectangleHighlightedPoints } from '../utils/get-selected-datapoints.js';

export const XYChartRectanglePoint = memo(() => {
  const allRectangleDatapoints = useTooltipMetadata('rectangle');
  const selectState = useSelectState();
  const { width, height } = useGridPlotSize();
  const axisIdToScales = useXYChartScales();
  const rectSelection = useXYChartRectSelection();
  const { datapointMetadataMap } = useXYChartData();
  const { axisIdToScaleDomain, axes } = useXYChartData();

  const {
    seriesToMetadata: barSeriesMeta,
    filteredSeriesWithDatapoints: barSeriesWithDatapoints,
  } = useBarData();
  const {
    seriesToMetadata: rectSeriesMeta,
    filteredSeriesWithDatapoints: rectSeriesWithDatapoints,
  } = useRectData();

  const highlightedRects = buildRectangleHighlightedPoints({
    allRectangleDatapoints,
    selectState,
    rectSelection,
    width,
    rectSeriesWithDatapoints,
    barSeriesWithDatapoints,
    axisIdToScales,
    barSeriesMeta,
    rectSeriesMeta,
    datapointMetadataMap,
    axisIdToScaleDomain,
    axes,
  });

  if (!highlightedRects.length) {
    return null;
  }

  return (
    <div className={datapointIndicatorLayout}>
      <CanvasContext.Provider
        value={{
          shapeRefs: new Map<string, MutableRefObject<CanvasShape>>(),
        }}
      >
        <Canvas
          width={width + RECT_STROKE_WIDTH}
          height={height + RECT_STROKE_WIDTH}
        >
          <HighlightedRectangleCanvas points={highlightedRects} />
        </Canvas>
      </CanvasContext.Provider>
    </div>
  );
});

XYChartRectanglePoint.displayName = 'XYChartRectanglePoint';
